import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';
import '../App.css';
import DescriptionDisplay from '../components/DescriptionDisplay';
import DonutStat from '../components/DonutStat';
import PageActions from '../components/PageActions';
import 'leaflet/dist/leaflet.css';
import TripMap from '../components/TripMap';
import { buildArcPolyline, DEFAULT_ROUTE_PATH_OPTIONS } from '../utils/mapArcs';
import { buildTripRouteStops } from '../utils/tripRoute';
import { sortByDate } from '../utils/sort';
import { buildIsTravelType } from '../utils/activityTypes';
import WrappedStats from '../components/WrappedStats';

function Home() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState('');
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityCategories, setActivityCategories] = useState([]);
  const [newTripName, setNewTripName] = useState('');
  const [isCreateTripOpen, setIsCreateTripOpen] = useState(false);
  const [destinationPoints, setDestinationPoints] = useState([]);
  const [mapTheme, setMapTheme] = useState('dark');
  const [mapTripId, setMapTripId] = useState('all');
  const [mapTripDestinations, setMapTripDestinations] = useState([]);
  const [mapTripActivities, setMapTripActivities] = useState([]);
  const [visitedIso2, setVisitedIso2] = useState(new Set());
  const [countriesGeoJson, setCountriesGeoJson] = useState(null);
  const [completedDistanceKm, setCompletedDistanceKm] = useState(null);
  const [routeLegTrips, setRouteLegTrips] = useState([]);
  const [unescoCount, setUnescoCount] = useState(null);

  useEffect(() => {
    fetchTrips();
    fetchDestinationPoints();
    fetchVisitedCountries();
    fetchCountriesGeoJson();
    fetchCompletedDistance();
    fetchRouteLegs();
    fetchActivityTypes();
    fetchActivityCategories();
    fetchUnescoCount();
  }, []);

  const fetchUnescoCount = async () => {
    try {
      const labelsRes = await fetch(apiUrl('/api/labels'));
      const labels = await labelsRes.json().catch(() => []);
      const unescoLabel = labels.find(l => l.name === 'UNESCO World Heritage Site');
      if (!unescoLabel) { setUnescoCount(0); return; }
      // Count distinct activities + wishlist items with this label
      const [actRes] = await Promise.all([
        fetch(apiUrl(`/api/labels/${unescoLabel.label_id}/activities`)).catch(() => null),
      ]);
      const actData = actRes?.ok ? await actRes.json().catch(() => []) : [];
      setUnescoCount(Array.isArray(actData) ? actData.length : 0);
    } catch { setUnescoCount(0); }
  };

  const fetchActivityTypes = async () => {
    try {
      const res = await fetch(apiUrl('/api/activity_types'));
      const data = await res.json();
      setActivityTypes(Array.isArray(data) ? data : []);
    } catch {
      setActivityTypes([]);
    }
  };

  const fetchActivityCategories = async () => {
    try {
      const res = await fetch(apiUrl('/api/activity_categories'));
      const data = await res.json();
      setActivityCategories(Array.isArray(data) ? data : []);
    } catch {
      setActivityCategories([]);
    }
  };

  const fetchCompletedDistance = async () => {
    try {
      const response = await fetch(apiUrl('/api/stats/distance?completed_only=true'));
      const data = await response.json();
      const total = Number(data?.total_km);
      setCompletedDistanceKm(Number.isFinite(total) ? total : 0);
    } catch (error) {
      console.error('Error fetching distance stats:', error);
      setCompletedDistanceKm(null);
    }
  };

  const completedDistanceLabel = useMemo(() => {
    if (completedDistanceKm === null) return null;
    const rounded = Math.round(Number(completedDistanceKm) || 0);
    return `${rounded.toLocaleString()} km traveled`;
  }, [completedDistanceKm]);

  const fetchVisitedCountries = async () => {
    try {
      const response = await fetch(apiUrl('/api/visited/countries'));
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setVisitedIso2(new Set(list.map(c => String(c || '').toUpperCase()).filter(Boolean)));
    } catch (error) {
      console.error('Error fetching visited countries:', error);
      setVisitedIso2(new Set());
    }
  };

  const fetchCountriesGeoJson = async () => {
    try {
      const response = await fetch('https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson');
      if (!response.ok) throw new Error('Failed to fetch countries GeoJSON');
      const data = await response.json();
      setCountriesGeoJson(data);
    } catch (error) {
      console.error('Error fetching countries GeoJSON:', error);
      setCountriesGeoJson(null);
    }
  };

  useEffect(() => {
    if (selectedTrip) {
      fetchActivities(selectedTrip);
    } else {
      setActivities([]);
    }
  }, [selectedTrip]);

  useEffect(() => {
    if (!mapTripId || mapTripId === 'all') {
      setMapTripDestinations([]);
      setMapTripActivities([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const [destRes, actRes] = await Promise.all([
          fetch(apiUrl(`/api/trips/${mapTripId}/destinations`)),
          fetch(apiUrl(`/api/trips/${mapTripId}/activities`)),
        ]);

        const destData = await destRes.json().catch(() => []);
        const actData = await actRes.json().catch(() => []);
        if (cancelled) return;

        setMapTripDestinations(Array.isArray(destData) ? destData : []);
        setMapTripActivities(Array.isArray(actData) ? actData : []);
      } catch {
        if (cancelled) return;
        setMapTripDestinations([]);
        setMapTripActivities([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mapTripId]);

  const sortedMapTripDestinations = useMemo(
    () => sortByDate(mapTripDestinations, 'start_date'),
    [mapTripDestinations]
  );

  const isTravelTypeId = useMemo(
    () => buildIsTravelType(activityTypes, activityCategories),
    [activityTypes, activityCategories]
  );

  const mapTripRoutePoints = useMemo(() => {
    if (!mapTripId || mapTripId === 'all') return [];
    const list = Array.isArray(destinationPoints) ? destinationPoints : [];
    return list.filter(p => String(p?.trip_id) === String(mapTripId));
  }, [destinationPoints, mapTripId]);

  const mapTripRoute = useMemo(() => {
    if (!mapTripId || mapTripId === 'all') return { stops: [], coords: [] };
    return buildTripRouteStops({
      sortedDestinations: sortedMapTripDestinations,
      routePoints: mapTripRoutePoints,
      activities: mapTripActivities,
      isTravelTypeId,
      mode: 'auto',
    });
  }, [mapTripId, sortedMapTripDestinations, mapTripRoutePoints, mapTripActivities, isTravelTypeId]);

  const mapTripRouteArcs = useMemo(() => {
    const coords = Array.isArray(mapTripRoute?.coords) ? mapTripRoute.coords : [];
    const arcs = [];
    for (let i = 0; i < coords.length - 1; i += 1) {
      const pts = buildArcPolyline(coords[i], coords[i + 1]);
      if (pts.length) arcs.push(pts);
    }
    return arcs;
  }, [mapTripRoute]);

  const fetchTrips = async () => {
    try {
      const response = await fetch(apiUrl('/api/trips'));
      const data = await response.json();
      setTrips(data);
    } catch (error) {
      console.error('Error fetching trips:', error);
    }
  };

  const fetchDestinationPoints = async () => {
    try {
      const response = await fetch(apiUrl('/api/destinations/points'));
      const data = await response.json();
      setDestinationPoints(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching destination points:', error);
      setDestinationPoints([]);
    }
  };

  const fetchRouteLegs = async () => {
    try {
      const response = await fetch(apiUrl('/api/routes/legs?completed_only=true'));
      const data = await response.json();
      setRouteLegTrips(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching route legs:', error);
      setRouteLegTrips([]);
    }
  };

  const visibleDestinationPoints = Array.isArray(destinationPoints)
    ? destinationPoints.filter(p => !!p.trip_completed || !!p.destination_completed)
    : [];

  const visibleTripIds = new Set(visibleDestinationPoints.map(p => String(p.trip_id)));
  const visibleTrips = Array.isArray(trips) ? trips.filter(t => visibleTripIds.has(String(t.trip_id))) : [];

  useEffect(() => {
    const ids = new Set(
      (Array.isArray(destinationPoints) ? destinationPoints : [])
        .filter(p => !!p.trip_completed || !!p.destination_completed)
        .map(p => String(p.trip_id))
    );
    if (mapTripId !== 'all' && !ids.has(String(mapTripId))) {
      setMapTripId('all');
    }
  }, [mapTripId, destinationPoints]);

  const filteredPoints = (mapTripId === 'all')
    ? visibleDestinationPoints
    : visibleDestinationPoints.filter(p => String(p.trip_id) === String(mapTripId));

  const filteredLegTrips = useMemo(() => {
    const tripsArr = Array.isArray(routeLegTrips) ? routeLegTrips : [];
    if (mapTripId === 'all') return tripsArr;
    return tripsArr.filter(t => String(t.trip_id) === String(mapTripId));
  }, [routeLegTrips, mapTripId]);

  const filteredLegs = useMemo(() => {
    const out = [];
    (Array.isArray(filteredLegTrips) ? filteredLegTrips : []).forEach((t) => {
      const legs = Array.isArray(t?.legs) ? t.legs : [];
      legs.forEach((leg) => {
        const from = Array.isArray(leg?.from) ? leg.from : null;
        const to = Array.isArray(leg?.to) ? leg.to : null;
        if (!from || !to || from.length !== 2 || to.length !== 2) return;
        const fromLat = Number(from[0]);
        const fromLon = Number(from[1]);
        const toLat = Number(to[0]);
        const toLon = Number(to[1]);
        if (![fromLat, fromLon, toLat, toLon].every(Number.isFinite)) return;
        out.push({
          trip_id: t.trip_id,
          trip_name: t.trip_name,
          activity_id: leg.activity_id,
          title: leg.title,
          distance_km: leg.distance_km,
          positions: [
            [fromLat, fromLon],
            [toLat, toLon],
          ],
        });
      });
    });
    return out;
  }, [filteredLegTrips]);

  const fitPoints = useMemo(() => {
    const pts = [];
    (Array.isArray(filteredPoints) ? filteredPoints : []).forEach((p) => {
      const lat = Number(p?.lat);
      const lon = Number(p?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      pts.push([lat, lon]);
    });
    (Array.isArray(filteredLegs) ? filteredLegs : []).forEach((l) => {
      const a = l.positions?.[0];
      const b = l.positions?.[1];
      if (Array.isArray(a) && a.length === 2 && a.every(Number.isFinite)) pts.push(a);
      if (Array.isArray(b) && b.length === 2 && b.every(Number.isFinite)) pts.push(b);
    });
    return pts;
  }, [filteredPoints, filteredLegs]);

  const mapFitPoints = useMemo(() => {
    if (mapTripId !== 'all') return Array.isArray(mapTripRoute?.coords) ? mapTripRoute.coords : [];
    return fitPoints;
  }, [mapTripId, mapTripRoute, fitPoints]);

  const geoJsonStyle = useCallback((feature) => {
    const props = feature && feature.properties ? feature.properties : {};
    const iso2 = String(props.ISO_A2_EH || props.ISO_A2 || props.iso_a2 || '').toUpperCase().replace(/^-99$/, '');
    const isVisited = iso2 && iso2 !== '-99' && visitedIso2.has(iso2);
    return {
      color: '#d4a84b',
      weight: 1,
      fillColor: '#e6b800',
      fillOpacity: isVisited ? 0.28 : 0.0,
    };
  }, [visitedIso2]);

  const worldStats = (() => {
    const feats = Array.isArray(countriesGeoJson?.features) ? countriesGeoJson.features : [];
    const iso2ToContinent = new Map();
    const allIso2 = new Set();
    const allContinents = new Set();

    feats.forEach((f) => {
      const props = f && f.properties ? f.properties : {};
      const iso2 = String(props.ISO_A2_EH || props.ISO_A2 || props.iso_a2 || '').toUpperCase().trim();
      if (!iso2 || iso2 === '-99') return;
      allIso2.add(iso2);

      const cont = String(props.CONTINENT || props.continent || '').trim();
      if (cont) {
        allContinents.add(cont);
        iso2ToContinent.set(iso2, cont);
      }
    });

    const visitedCountries = new Set();
    visitedIso2.forEach((cc) => {
      const iso2 = String(cc || '').toUpperCase().trim();
      if (allIso2.has(iso2)) visitedCountries.add(iso2);
    });

    const visitedContinents = new Set();
    visitedCountries.forEach((iso2) => {
      const cont = iso2ToContinent.get(iso2);
      if (cont) visitedContinents.add(cont);
    });

    return {
      totalCountries: allIso2.size,
      visitedCountries: visitedCountries.size,
      totalContinents: allContinents.size,
      visitedContinents: visitedContinents.size,
    };
  })();

  const fetchActivities = async (tripId) => {
    try {
      const response = await fetch(apiUrl(`/api/trip_activities?trip_id=${tripId}`));
      const data = await response.json();
      setActivities(data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const createTrip = async () => {
    if (!newTripName.trim()) return;
    try {
      const response = await fetch(apiUrl('/api/trips'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ trip_name: newTripName }),
      });
      if (response.ok) {
        const newTrip = await response.json();
        setTrips((prev) => [...prev, newTrip]);
        setNewTripName('');
        setIsCreateTripOpen(false);
        // Navigate to trip setup
        navigate(`/trip-setup/${newTrip.trip_id}`);
      } else {
        const error = await response.json();
        console.error('Error creating trip:', error);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };

  return (
    <div className="App">
      <section className="tp-hero">
        <p className="tp-hero__kicker">Plan • Organize • Go</p>
        <h1 className="tp-hero__title">Travel Planner</h1>
        <p className="tp-hero__subtitle">
          Build a trip itinerary by destination and date, then track activities with categories, types, and times.
        </p>
        <PageActions
          actions={[
            { label: 'Manage Trips', to: '/trips', variant: 'solid' },
            { label: 'Developer DB View', to: '/database', variant: 'ghost' },
          ]}
        />
      </section>

      <WrappedStats />

      <section className="tp-section">
        <div className="tp-grid">
          <div className="tp-col-12">
            <div className="tp-card">
              <h2 className="tp-section__title">Open a Trip</h2>

              <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
                <div className="tp-field" style={{ flex: 1, marginBottom: 0 }}>
                  <span className="tp-label">Select Trip</span>
                  <select value={selectedTrip} onChange={(e) => setSelectedTrip(e.target.value)}>
                    <option value="">Choose a trip</option>
                    {trips.map(trip => (
                      <option key={trip.trip_id} value={trip.trip_id}>{trip.trip_name}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  className="tp-btn--ghost"
                  onClick={() => {
                    setNewTripName('');
                    setIsCreateTripOpen(true);
                  }}
                >
                  Create a trip
                </button>
              </div>

              <div className="tp-row">
                {selectedTrip && (
                  <Link to={`/itinerary/${selectedTrip}`}>
                    <button>Go to Trip</button>
                  </Link>
                )}
                {selectedTrip && (
                  <Link to={`/trip-setup/${selectedTrip}`}>
                    <button className="tp-btn--ghost">Edit Destinations</button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {isCreateTripOpen && (
        <div
          className="tp-card tp-card--tight"
          style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            minWidth: 520,
            maxWidth: 'calc(100vw - 32px)',
            width: 520,
            background: 'var(--tp-bg)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'grid', gap: 10 }}>
            <div className="tp-field" style={{ marginBottom: 0 }}>
              <span className="tp-label">Trip Name</span>
              <input
                type="text"
                placeholder="e.g., Netherlands 2026"
                value={newTripName}
                onChange={(e) => setNewTripName(e.target.value)}
                autoFocus
              />
            </div>

            <div className="tp-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={createTrip} disabled={!newTripName.trim()}>
                Create trip
              </button>
              <button
                type="button"
                className="tp-btn--ghost"
                onClick={() => {
                  setIsCreateTripOpen(false);
                  setNewTripName('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="tp-section">
        <div className="tp-card">
          <div className="tp-row" style={{ justifyContent: 'space-between' }}>
            <h2 className="tp-section__title" style={{ margin: 0 }}>Destination Map</h2>
            <div className="tp-row" style={{ gap: 10 }}>
              <label className="tp-switch" title="Toggle map theme">
                <input
                  type="checkbox"
                  checked={mapTheme === 'dark'}
                  onChange={(e) => setMapTheme(e.target.checked ? 'dark' : 'light')}
                />
                <span className="tp-switch__track" aria-hidden="true">
                  <span className="tp-switch__thumb" aria-hidden="true" />
                </span>
                <span className="tp-switch__label">Dark</span>
              </label>

              <div className="tp-field" style={{ margin: 0, minWidth: 220 }}>
                <select value={mapTripId} onChange={(e) => setMapTripId(e.target.value)}>
                  <option value="all">All trips</option>
                  {visibleTrips.map(t => (
                    <option key={t.trip_id} value={t.trip_id}>{t.trip_name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="tp-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--tp-muted)' }}>Traveler stats</div>
                <div style={{ fontSize: 13, marginTop: 4, color: 'var(--tp-muted)' }}>
                  {worldStats.visitedCountries} countries visited • {worldStats.visitedContinents} continents visited
                  {completedDistanceLabel ? ` • ${completedDistanceLabel}` : ''}
                  {unescoCount > 0 ? ` • ${unescoCount} UNESCO site${unescoCount === 1 ? '' : 's'}` : ''}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
              <DonutStat
                label="Countries visited"
                value={worldStats.visitedCountries}
                total={worldStats.totalCountries}
              />
              <DonutStat
                label="Continents visited"
                value={worldStats.visitedContinents}
                total={worldStats.totalContinents}
              />
            </div>
          </div>

          {mapTripId === 'all' && filteredPoints.length === 0 ? (
            <p style={{ color: 'var(--tp-muted)', margin: '10px 0 0' }}>
              No destination locations for completed trips or completed destinations yet. Mark a trip (or a destination) completed to see it here.
            </p>
          ) : (
            <div
              className={`tp-map ${mapTheme === 'dark' ? 'tp-map--dark' : 'tp-map--light'}`}
              style={{ marginTop: 12, height: 420, borderRadius: 16, overflow: 'hidden' }}
            >
              <TripMap
                center={[20, 0]}
                zoom={3}
                fitPoints={mapFitPoints}
                fitKey={`${mapTripId}:${mapFitPoints.length}`}
                theme={mapTheme}
                height={420}
                markers={
                  mapTripId === 'all'
                    ? (filteredPoints || [])
                        .map((p) => ({
                          lat: Number(p.lat),
                          lon: Number(p.lon),
                          title: p.destination_name,
                          popup: p.trip_name,
                          radius: 3,
                        }))
                        .filter((m) => Number.isFinite(m.lat) && Number.isFinite(m.lon))
                    : (Array.isArray(mapTripRoute?.stops) ? mapTripRoute.stops : []).map((s, idx) => ({
                        lat: s.lat,
                        lon: s.lon,
                        title: s.destination_name,
                        popup: `Stop ${idx + 1}`,
                        radius: 4,
                      }))
                }
                polylines={
                  mapTripId === 'all'
                    ? (filteredLegs || [])
                        .map((l) => ({
                          positions: buildArcPolyline(l.positions?.[0], l.positions?.[1]),
                          pathOptions: DEFAULT_ROUTE_PATH_OPTIONS,
                        }))
                        .filter((pl) => pl.positions.length > 0)
                    : (mapTripRouteArcs || []).map((pts) => ({
                        positions: pts,
                        pathOptions: DEFAULT_ROUTE_PATH_OPTIONS,
                      }))
                }
                geoJsonOverlay={countriesGeoJson}
                geoJsonStyle={geoJsonStyle}
              />
            </div>
          )}
        </div>
      </section>

      {selectedTrip && (
        <section className="tp-section">
          <div className="tp-card">
            <div className="tp-row" style={{ justifyContent: 'space-between' }}>
              <h2 className="tp-section__title" style={{ margin: 0 }}>Activities</h2>
              <span className="tp-pill">Trip selected</span>
            </div>
            {activities.length > 0 ? (
              <table className="tp-table" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map(activity => (
                    <tr key={activity.activity_id}>
                      <td>{activity.title}</td>
                      <td><DescriptionDisplay text={activity.description} emptyFallback="" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: 'var(--tp-muted)', margin: '10px 0 0' }}>No activities yet.</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;
